/**
 * Passage highlighter — pure logic. No DOM, no React.
 *
 * The student arms a coloured pen and drags across the reading passage to highlight
 * evidence. Highlights are stored as sorted, merged, non-overlapping [start, end)
 * character ranges into the passage string.
 *
 * Everything here is deliberately DOM-free: jsdom has no Selection/Range geometry and
 * `document.elementFromPoint` always returns null, so this module is where the logic can
 * actually be tested. The DOM layer on top of it stays as thin as possible.
 *
 * WORD SNAPPING — ranges always begin at a word's start and end at a word's end. A real
 * highlighter has no sub-word precision, and PSLE evidence is phrase-level. This also means
 * a range boundary can never fall inside a word, which is what lets buildLineRuns() emit
 * whole word spans nested inside colour runs.
 *
 * LINE NUMBERS — PSLE passages prefix fixed physical lines with "1.", "5.", "10." and
 * students refer to "line 7". Those prefix tokens are marked non-highlightable so a stray
 * tap on one does nothing. (A multi-line drag that sweeps past one will still tint it; that
 * is an accepted, rare cosmetic case — punching them out would mean splitting ranges.)
 */

export type HighlightColor = 'yellow' | 'green' | 'pink';

/** A highlight over [start, end) character offsets of the passage string. */
export interface HighlightRange {
  start: number;
  end: number;
  color: HighlightColor;
}

/**
 * Single source of truth for the pen colours.
 *
 * These are applied as INLINE background styles, never as `bg-${color}` classes — Tailwind's
 * JIT purges dynamically-constructed class names, so a class built at runtime would be
 * stripped from the production bundle.
 *
 * Contrast against the passage text colour otter-earth-800 (#78350f), WCAG AA floor 4.5:1:
 *   yellow #fde68a → 7.29:1   (passage-200)
 *   green  #bbf7d0 → 7.49:1   (student-200)
 *   pink   #fbcfe8 → 6.57:1   (Tailwind pink-200)
 */
export const HIGHLIGHT_COLORS: Record<HighlightColor, string> = {
  yellow: '#fde68a',
  green: '#bbf7d0',
  pink: '#fbcfe8',
};

export const HIGHLIGHT_COLOR_ORDER: HighlightColor[] = ['yellow', 'green', 'pink'];

export const STORAGE_KEY = 'thinkotter:passage-highlights:v1';

/** Cap on how many sessions' highlights we keep in localStorage. */
export const MAX_STORED_SESSIONS = 20;

function isHighlightColor(value: unknown): value is HighlightColor {
  return typeof value === 'string' && value in HIGHLIGHT_COLORS;
}

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

export type TokenType = 'word' | 'space' | 'newline';

export interface Token {
  type: TokenType;
  text: string;
  start: number;
  /** Exclusive. */
  end: number;
  /** False for newlines, whitespace, and line-number prefixes ("7."). */
  highlightable: boolean;
}

const LINE_NUMBER_PREFIX = /^\d+\.$/;

/**
 * Split the passage into words, whitespace runs, and newlines.
 *
 * Concatenating every token's text reproduces the passage exactly — the render layer relies
 * on this to keep `whitespace-pre` line geometry (and therefore line references) unchanged.
 */
export function tokenize(passage: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  // True until the first word of a physical line is consumed. Whitespace does not clear it,
  // so " 7. The otter" still recognises "7." as a line-number prefix.
  let atLineStart = true;

  while (i < passage.length) {
    const char = passage[i];

    if (char === '\n') {
      tokens.push({ type: 'newline', text: '\n', start: i, end: i + 1, highlightable: false });
      atLineStart = true;
      i += 1;
      continue;
    }

    if (char === ' ' || char === '\t') {
      let j = i;
      while (j < passage.length && (passage[j] === ' ' || passage[j] === '\t')) j += 1;
      tokens.push({ type: 'space', text: passage.slice(i, j), start: i, end: j, highlightable: false });
      i = j;
      continue;
    }

    let j = i;
    while (j < passage.length && !/[\s]/.test(passage[j])) j += 1;
    const text = passage.slice(i, j);
    const isLineNumber = atLineStart && LINE_NUMBER_PREFIX.test(text);
    tokens.push({ type: 'word', text, start: i, end: j, highlightable: !isLineNumber });
    atLineStart = false;
    i = j;
  }

  return tokens;
}

// ---------------------------------------------------------------------------
// Range algebra
// ---------------------------------------------------------------------------

/**
 * Expand two word-start offsets into a word-aligned range.
 *
 * `a` and `b` are the `data-offset` values of the anchor and focus word spans, in either
 * order. Returns null if either offset does not name a highlightable word — which is how a
 * tap on a line-number prefix becomes a no-op.
 */
export function snapRange(tokens: Token[], a: number, b: number): { start: number; end: number } | null {
  const anchor = tokens.find((t) => t.start === a && t.highlightable);
  const focus = tokens.find((t) => t.start === b && t.highlightable);
  if (!anchor || !focus) return null;

  return {
    start: Math.min(anchor.start, focus.start),
    end: Math.max(anchor.end, focus.end),
  };
}

/** The range covering `offset`, or null. */
export function offsetInRange(ranges: HighlightRange[], offset: number): HighlightRange | null {
  return ranges.find((r) => offset >= r.start && offset < r.end) ?? null;
}

/** The colour at `offset`, or null. */
export function colorAt(ranges: HighlightRange[], offset: number): HighlightColor | null {
  return offsetInRange(ranges, offset)?.color ?? null;
}

function normalize(ranges: HighlightRange[]): HighlightRange[] {
  const sorted = [...ranges].filter((r) => r.end > r.start).sort((x, y) => x.start - y.start);
  const merged: HighlightRange[] = [];

  for (const range of sorted) {
    const prev = merged[merged.length - 1];
    // Only coalesce touching/overlapping runs of the SAME colour — two different pens sitting
    // side by side must stay two ranges, or erasing one would wipe the other.
    if (prev && prev.color === range.color && range.start <= prev.end) {
      prev.end = Math.max(prev.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }

  return merged;
}

/**
 * Add `next`, overwriting whatever it covers. The new pen always wins on overlap, so dragging
 * a green pen over a yellow highlight recolours it rather than layering.
 */
export function addHighlight(ranges: HighlightRange[], next: HighlightRange): HighlightRange[] {
  if (next.end <= next.start) return normalize(ranges);

  const survivors: HighlightRange[] = [];

  for (const range of ranges) {
    // Disjoint — keep whole.
    if (range.end <= next.start || range.start >= next.end) {
      survivors.push(range);
      continue;
    }
    // Keep the parts of `range` that `next` does not cover. Both may be non-empty when
    // `next` lands strictly inside `range`, splitting it in two.
    if (range.start < next.start) survivors.push({ ...range, end: next.start });
    if (range.end > next.end) survivors.push({ ...range, start: next.end });
  }

  survivors.push({ ...next });
  return normalize(survivors);
}

/** True when everything strictly between two offsets is spaces/tabs — never a newline. */
function isWhitespaceGap(tokens: Token[], from: number, to: number): boolean {
  if (to <= from) return false;
  // Range boundaries always fall on word boundaries, so every overlapping token lies wholly
  // inside the gap.
  const covering = tokens.filter((t) => t.end > from && t.start < to);
  return covering.length > 0 && covering.every((t) => t.type === 'space');
}

/**
 * Join same-colour ranges that are separated only by spaces.
 *
 * Highlighting "the cold" and then "river without" leaves offsets 8-35 and 36-155: the space at
 * 35 belongs to neither, so the two <mark>s render with a one-character white gap through the
 * middle of the band. Bridging the gap makes consecutive highlights read as one stroke, the way
 * a real highlighter would.
 *
 * A gap containing a newline is never bridged, so marks still stop at line ends.
 */
export function bridgeWhitespaceGaps(ranges: HighlightRange[], tokens: Token[]): HighlightRange[] {
  if (ranges.length < 2) return ranges;

  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: HighlightRange[] = [{ ...sorted[0] }];

  for (let i = 1; i < sorted.length; i += 1) {
    const prev = merged[merged.length - 1];
    const range = sorted[i];

    if (prev.color === range.color && isWhitespaceGap(tokens, prev.end, range.start)) {
      prev.end = Math.max(prev.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }

  return merged;
}

/** Remove the entire contiguous range sitting under `offset`. Tap-to-erase. */
export function eraseAt(ranges: HighlightRange[], offset: number): HighlightRange[] {
  const target = offsetInRange(ranges, offset);
  if (!target) return ranges;
  return ranges.filter((r) => r !== target);
}

/**
 * Drop anything that cannot apply to a passage of `length` characters, and anything with an
 * unrecognised colour. Guards against a stored payload from an older passage or a hand-edited
 * localStorage value.
 */
export function clampRanges(ranges: unknown, length: number): HighlightRange[] {
  if (!Array.isArray(ranges)) return [];

  const valid = ranges.filter((r): r is HighlightRange => {
    if (!r || typeof r !== 'object') return false;
    const { start, end, color } = r as Partial<HighlightRange>;
    return (
      Number.isInteger(start) &&
      Number.isInteger(end) &&
      isHighlightColor(color) &&
      (start as number) >= 0 &&
      (end as number) <= length &&
      (end as number) > (start as number)
    );
  });

  return normalize(valid);
}

// ---------------------------------------------------------------------------
// Render model
// ---------------------------------------------------------------------------

/** A run of adjacent tokens sharing one colour (or none). */
export interface Run {
  color: HighlightColor | null;
  tokens: Token[];
}

/**
 * Group the passage's tokens into physical lines, and each line into colour runs.
 *
 * Newline tokens are dropped — they are implied by the outer array. The render layer emits a
 * literal "\n" between lines, so under `whitespace-pre` the output is character-identical to
 * the raw passage.
 *
 * Runs exist so that a contiguous highlight is ONE <mark> element rather than one per word;
 * abutting <mark>s can show subpixel seams between them.
 */
export function buildLineRuns(tokens: Token[], ranges: HighlightRange[]): Run[][] {
  const lines: Run[][] = [[]];

  for (const token of tokens) {
    if (token.type === 'newline') {
      lines.push([]);
      continue;
    }

    const line = lines[lines.length - 1];
    const color = colorAt(ranges, token.start);
    const current = line[line.length - 1];

    if (current && current.color === color) {
      current.tokens.push(token);
    } else {
      line.push({ color, tokens: [token] });
    }
  }

  return lines;
}

// ---------------------------------------------------------------------------
// Storage serialization
// ---------------------------------------------------------------------------

export interface StoredSession {
  ranges: HighlightRange[];
  updatedAt: number;
}

export type HighlightStoreData = Record<string, StoredSession>;

/** Tolerant parse: any malformed payload degrades to "no highlights" rather than throwing. */
export function parseStore(raw: string | null): HighlightStoreData {
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

  const result: HighlightStoreData = {};
  for (const [sessionId, entry] of Object.entries(parsed as Record<string, unknown>)) {
    if (!entry || typeof entry !== 'object') continue;
    const { ranges, updatedAt } = entry as Partial<StoredSession>;
    if (!Array.isArray(ranges)) continue;
    result[sessionId] = {
      // Bound-checking happens later in clampRanges(), once the passage length is known.
      ranges: ranges.filter((r) => r && isHighlightColor((r as HighlightRange).color)),
      updatedAt: typeof updatedAt === 'number' ? updatedAt : 0,
    };
  }

  return result;
}

/** Keep only the `max` most recently touched sessions, so the key cannot grow forever. */
export function pruneToRecent(data: HighlightStoreData, max: number): HighlightStoreData {
  const entries = Object.entries(data);
  if (entries.length <= max) return data;

  const kept = entries.sort(([, a], [, b]) => b.updatedAt - a.updatedAt).slice(0, max);
  return Object.fromEntries(kept);
}
